import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, PawPrint, Contact, Mail, Phone, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { ContactExchangeModal } from './ContactExchangeModal';
import { useContactExchange } from '@/hooks/useContactExchange';
import { useTranslation } from 'react-i18next';
import { useMatching } from '@/hooks/useMatching';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface MatchChatCardProps {
  match: any;
  onOpenChat: () => void;
}

export const MatchChatCard = ({ match, onOpenChat }: MatchChatCardProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { requests } = useContactExchange(match.id);
  const { deleteMatch } = useMatching();
  
  // Determine which pet to display (the other user's pet)
  const displayPet = match.user_1_id === user?.id ? match.pet_2 : match.pet_1;
  
  // Check contact exchange status
  const contactRequest = requests?.[0]; // Get the most recent request
  const isRequester = contactRequest?.requester_user_id === user?.id;
  const isReceiver = contactRequest?.receiver_user_id === user?.id;
  
  // Check if user has already shared their info
  const userHasShared = isRequester ? contactRequest?.requester_approved : contactRequest?.receiver_approved;
  const otherHasShared = isRequester ? contactRequest?.receiver_approved : contactRequest?.requester_approved;
  
  // Get the other user's contact data
  const otherUserContactData = isRequester 
    ? contactRequest?.exchange_data?.receiver 
    : contactRequest?.exchange_data?.requester;

  const handleDelete = async () => {
    await deleteMatch.mutateAsync(match.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={onOpenChat}>
      <div className="flex items-center gap-4">
        {/* Pet Avatar */}
        <Avatar className="h-14 w-14">
          {displayPet?.photo_url ? (
            <AvatarImage src={displayPet.photo_url} alt={displayPet.name} />
          ) : (
            <AvatarFallback className="bg-pink-100">
              <PawPrint className="h-7 w-7 text-pink-500" />
            </AvatarFallback>
          )}
        </Avatar>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h3 className="font-semibold text-lg">{displayPet?.name}</h3>
            <Badge variant="secondary" className="flex-shrink-0">
              <MessageCircle className="h-3 w-3 mr-1" />
              {t('matchCard.chat')}
            </Badge>
          </div>

          <div className="text-sm text-muted-foreground">
            {match.chat_room?.last_message_at ? (
              <span>{t('matchInbox.chat.lastMessage')} {formatDistanceToNow(new Date(match.chat_room.last_message_at), { addSuffix: true })}</span>
            ) : (
              <span>{t('matchInbox.chat.startConversation')}</span>
            )}
          </div>

          {/* Contact Information Display */}
          {otherHasShared && otherUserContactData && (
            <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200" onClick={(e) => e.stopPropagation()}>
              <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1">
                <Contact className="h-4 w-4" />
                {t('contactExchange.shared.title')}
              </p>
              <div className="text-xs space-y-1 text-green-900">
                {otherUserContactData.email && (
                  <p className="flex items-center gap-2">
                    <Mail className="h-3 w-3" />
                    {otherUserContactData.email}
                  </p>
                )}
                {otherUserContactData.phone && (
                  <p className="flex items-center gap-2">
                    <Phone className="h-3 w-3" />
                    {otherUserContactData.phone}
                  </p>
                )}
                {otherUserContactData.whatsapp && (
                  <p className="flex items-center gap-2">
                    <MessageCircle className="h-3 w-3" />
                    {t('contactExchange.shared.whatsapp')}: {otherUserContactData.whatsapp}
                  </p>
                )}
                {otherUserContactData.notes && (
                  <p className="text-xs italic mt-2 text-green-700">{otherUserContactData.notes}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-shrink-0">
          {/* Si ambos han compartido, mostrar badge verde */}
          {userHasShared && otherHasShared && (
            <Badge variant="outline" className="bg-green-50">
              <Contact className="h-3 w-3 mr-1" />
              {t('matchInbox.chat.exchangeContact')}
            </Badge>
          )}
          
          {/* Si no hay solicitud, mostrar botón para iniciar intercambio */}
          {!contactRequest && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowContactModal(true);
              }}
            >
              <Contact className="h-4 w-4 mr-1" />
              {t('matchInbox.chat.exchange')}
            </Button>
          )}
          
          {/* Si hay solicitud pero el usuario no ha compartido aún */}
          {contactRequest && !userHasShared && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowContactModal(true);
              }}
              className="bg-yellow-50 border-yellow-300 hover:bg-yellow-100"
            >
              <Contact className="h-4 w-4 mr-1" />
              {isReceiver ? t('contactExchange.actions.respond') : t('matchInbox.chat.exchange')}
            </Button>
          )}
          
          {/* Si el usuario ya compartió pero el otro no */}
          {contactRequest && userHasShared && !otherHasShared && (
            <Badge variant="secondary" className="bg-blue-50">
              ⏳ {t('matchInbox.status.pending')}
            </Badge>
          )}
          
          <Button variant="ghost" size="sm">
            <MessageCircle className="h-5 w-5" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteDialog(true);
            }}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {showContactModal && (
        <ContactExchangeModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          matchId={match.id}
          requestId={contactRequest?.id}
          isApproving={!!contactRequest && !userHasShared}
        />
      )}
    </Card>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('matchInbox.actions.delete')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('matchInbox.actions.deleteMatchDesc', { petName: displayPet?.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('matchModal.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-red-500 hover:bg-red-600"
          >
            {t('matchInbox.actions.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
